import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";

const initialState = {
  testTypes: [],
  testStandards: [],
  projectCode: null,
  basicStandards: [],
  list: [],
  loading: false,
  error: null,
  projectData: null,
};

export const fetchDropdownData = createAsyncThunk(
  "project/fetchDropdownData",
  async ({ term, companyID }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `Project/Project_DropDownList?term=${term}&companyID=${companyID}`,
      );
      if (response.success) {
        return { data: response.data, term };
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const getNextProjectCode = createAsyncThunk(
  "project/getNextProjectCode",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `Project/GetNextProjectCode?companyId=${companyId}`,
      );
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const getBasicStandards = createAsyncThunk(
  "project/getBasicStandards",
  async ({ testTypeId, companyId }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `Project/GetBasicStandards?testTypeId=${testTypeId}&companyId=${companyId}`,
      );
      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);
export const fetchProjects = createAsyncThunk(
  "project/fetchProjects",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `Project/Project_GetProjectList?CompanyId=${companyId}`,
      );
      if (response.success) {
        const projectsData = response.data || [];
        return projectsData.map((project) => ({
          ProjectID: project.Project_Id,
          ProjectName: project.Project_Name,
          ProjectCode: project.ProjectCode,
          TestType: project.TestTypes,
          TestName: project.TestNames,
          BasicStandard: project.BasicStandards,
          TestStandard: project.TestStandards,
          Status: project.Status || "Active",
          LastUpdated:
            project.LastUpdated || new Date().toISOString().split("T")[0],
        }));
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const createUpdateProject = createAsyncThunk(
  "project/createUpdateProject",
  async ({ projectData, companyId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("ProjectID", String(projectData.ProjectID));
      formData.append("ProjectName", projectData.ProjectName);
      formData.append("ProjectCode", projectData.ProjectCode);
      formData.append("ProjectCodePrefix", projectData.ProjectCodePrefix || "");
      formData.append("ProjectCodeSuffix", projectData.ProjectCodeSuffix || "");
      formData.append("SavedBy", String(projectData.SavedBy));
      formData.append("Status", projectData.Status);

      const testDetailsJson = JSON.stringify(projectData.TestDetails);
      formData.append("TestDetails", testDetailsJson);

      const response = await apiRequest(
        "POST",
        `Project/Project_CreateUpdateProject?CompanyId=${companyId}`,
        formData,
        true,
        false,
        false,
        false,
        projectData.ProjectID > 0,
      );

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      console.error("API Error:", err);
      console.error("Error Response:", err.response?.data);
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const getProjectData = createAsyncThunk(
  "project/getProjectData",
  async ({ projectId, companyId, testTypeId = 0 }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "GET",
        `Project/Project_GetProjectData?ProjectId=${projectId}&CompanyId=${companyId}&TestTypeId=${testTypeId}`,
      );
      if (response.success) {
        return { data: response.data, projectId };
      } else {
        return rejectWithValue(response.error);
      }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const closeProject = createAsyncThunk(
  "project/closeProject",
  async ({ projectId, companyId, createdBy = 0 }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "POST",
        `Project/CloseProject?projectId=${projectId}&companyId=${companyId}&createdBy=${createdBy}`,
      );
      if (response.success) {
        return { projectId, message: response.data?.message };
      } else {
        return rejectWithValue(response.error || response.data?.message || "Project could not be closed.");
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const reopenProject = createAsyncThunk(
  "project/reopenProject",
  async ({ projectId, companyId, remarks = "", createdBy = 0 }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(
        "POST",
        `Project/ReopenProject?projectId=${projectId}&companyId=${companyId}&Remarks=${encodeURIComponent(remarks)}&createdBy=${createdBy}`,
      );
      if (response.success) {
        return { projectId, message: response.data?.message };
      } else {
        return rejectWithValue(response.error || response.data?.message || "Project could not be reopened.");
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearProjectCode: (state) => {
      state.projectCode = null;
    },
    clearTestTypes: (state) => {
      state.testTypes = [];
    },
    clearTestStandards: (state) => {
      state.testStandards = [];
    },
    clearBasicStandards: (state) => {
      state.basicStandards = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.loading = false;
        const { data, term } = action.payload;
        if (term === "TESTTYPE") {
          state.testTypes = data || [];
        } else if (term === "TESTSTANDARD") {
          state.testStandards = data || [];
        }
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load dropdown data";
      })

      .addCase(getNextProjectCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNextProjectCode.fulfilled, (state, action) => {
        state.loading = false;
        state.projectCode = action.payload?.projectCode || "";
      })
      .addCase(getNextProjectCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to generate project code";
      })

      .addCase(getBasicStandards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBasicStandards.fulfilled, (state, action) => {
        state.loading = false;
        state.basicStandards = action.payload || [];
      })
      .addCase(getBasicStandards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load basic standards";
      })
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch projects";
      })
      .addCase(getProjectData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectData.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload;

        if (response?.data?.data) {
          const { testTypes, testStandards, basicStandards } = response.data.data;

          state.testTypes = testTypes || [];
          state.testStandards = testStandards || [];
          state.basicStandards = basicStandards || [];

          if (response.data.data.projectMaster && response.data.data.projectMaster.length > 0) {
            state.projectData = {
              master: response.data.data.projectMaster[0],
              testDetails: response.data.data.testDetails || []
            };
          }
        }
      })
      .addCase(getProjectData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load project data";
      })
      .addCase(closeProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(closeProject.fulfilled, (state, action) => {
        state.loading = false;
        // Optimistic UI update — Close moves In-Progress -> Completed.
        // fetchProjects is dispatched right after this to fully re-sync.
        const { projectId } = action.payload;
        const proj = state.list.find((p) => p.ProjectID === projectId);
        if (proj) proj.Status = "Completed";
      })
      .addCase(closeProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to close project";
      })
      .addCase(reopenProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reopenProject.fulfilled, (state, action) => {
        state.loading = false;
        // Optimistic UI update — Reopen moves Completed -> In-Progress.
        const { projectId } = action.payload;
        const proj = state.list.find((p) => p.ProjectID === projectId);
        if (proj) proj.Status = "In-Progress";
      })
      .addCase(reopenProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to reopen project";
      });
  },
});

export const {
  clearError,
  clearProjectCode,
  clearTestTypes,
  clearTestStandards,
  clearBasicStandards,
} = projectSlice.actions;

export default projectSlice.reducer;